import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import Icon from '../components/shared/Icon';

const TOTAL_STEPS = 10;

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    familyName: '',
    yourName: '',
    partnerName: '',
    kids: [{ name: '', age: '' }],
    identity: '',
    startingPoint: '',
    goals: [],
    parentingPriorities: [],
    timeCommitment: '',
    upcomingEvents: [],
  });

  const { user } = useAuth();
  const navigate = useNavigate();

  const handleComplete = async () => {
    if (!user) {
      toast.error('Please sign in to continue');
      return;
    }

    try {
      const familyId = `family-${user.uid}`;
      
      // Create family document
      await setDoc(doc(db, 'families', familyId), {
        name: formData.familyName,
        members: [user.uid],
        primaryContact: user.uid,
        kids: formData.kids.filter(kid => kid.name && kid.age),
        onboarding: {
          identity: formData.identity,
          startingPoint: formData.startingPoint,
          goals: formData.goals,
          parentingPriorities: formData.parentingPriorities,
          timeCommitment: formData.timeCommitment,
          upcomingEvents: formData.upcomingEvents,
          yourName: formData.yourName,
          partnerName: formData.partnerName,
        },
        createdAt: new Date(),
      });
      
      // Update user document
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: formData.yourName,
        familyId: familyId,
        onboardingComplete: true,
        createdAt: new Date(),
      });
      
      // Wait for the document to be readable (ensures Firestore has propagated)
      let attempts = 0;
      while (attempts < 20) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().familyId === familyId) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      toast.success('Welcome to your Culture!');
      
      // Longer delay to allow AuthContext listener to update and propagate
      // This is especially important for multi-domain scenarios
      console.log('Onboarding complete, waiting for AuthContext to update...', {
        userId: user.uid,
        familyId,
        hostname: window.location.hostname,
      });
      
      // Wait a bit longer to ensure the Firestore listener in AuthContext has updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force a navigation - if AuthContext hasn't updated, OnboardingRoute will handle it
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      console.error('Full error details:', error);
      
      // Check if it's a connection error
      if (error.code === 'unavailable' || error.message?.includes('CONNECTION_REFUSED') || error.message?.includes('Failed to get document')) {
        toast.error('Cannot connect to database. Please start Firebase emulators or check your connection.');
        console.error('💡 To fix: Run "firebase emulators:start" in a terminal');
      } else {
        toast.error('Failed to save your information. Please try again.');
      }
    }
  };

  const nextStep = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step === TOTAL_STEPS) {
        handleComplete();
      } else if (step >= 0 && step < TOTAL_STEPS) {
        nextStep();
      }
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  const progress = (step / TOTAL_STEPS) * 100;

  // Helper functions
  const addKid = () => {
    setFormData({
      ...formData,
      kids: [...formData.kids, { name: '', age: '' }],
    });
  };

  const removeKid = (index) => {
    if (formData.kids.length > 1) {
      setFormData({
        ...formData,
        kids: formData.kids.filter((_, i) => i !== index),
      });
    }
  };

  const updateKid = (index, field, value) => {
    const updatedKids = [...formData.kids];
    updatedKids[index] = { ...updatedKids[index], [field]: value };
    setFormData({ ...formData, kids: updatedKids });
  };

  const selectCard = (value, field) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleCheckbox = (value, field) => {
    const currentValues = formData[field];
    if (currentValues.includes(value)) {
      setFormData({
        ...formData,
        [field]: currentValues.filter((v) => v !== value),
      });
    } else {
      setFormData({
        ...formData,
        [field]: [...currentValues, value],
      });
    }
  };

  const toggleCheckboxMax = (value, field, max) => {
    const currentValues = formData[field];
    if (currentValues.includes(value)) {
      setFormData({
        ...formData,
        [field]: currentValues.filter((v) => v !== value),
      });
    } else if (currentValues.length < max) {
      setFormData({
        ...formData,
        [field]: [...currentValues, value],
      });
    } else {
      toast.error(`Please select only ${max} options`);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-[720px] w-full sm:w-[90%] py-8 sm:py-12 md:py-[60px]">
        {/* Progress Bar */}
        <div className="mb-6 sm:mb-8 md:mb-12">
            <div className="h-1 bg-border rounded-sm overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-[400ms]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-3 text-[13px] text-text-secondary text-right">
            Step {step} of {TOTAL_STEPS}
          </div>
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="animate-fadeIn">
            <div className="text-center py-6 sm:py-8 md:py-10">
              <div className="mb-4 sm:mb-6 flex justify-center">
                <img src="/logo512.png" alt="Untraditional" className="w-4 h-4 object-contain" />
              </div>
              <h1 className="font-sans text-2xl sm:text-3xl md:text-4xl font-semibold text-text-primary mb-3 sm:mb-4 md:mb-5 leading-tight px-2 tracking-tight">
                Design Your Family's Jewish Life
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-text-secondary mb-3 sm:mb-4 leading-relaxed max-w-[500px] mx-auto px-4">
                There's not one way to be Jewish. Create your own version.
              </p>
              <p className="text-sm sm:text-base text-primary font-medium mb-6 sm:mb-8 md:mb-10">
                No temple required. No judgment. Just your family's way.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10 md:mb-12">
              <div className="text-center">
                <div className="mb-3"><Icon name="🎉" size="2x" className="text-primary" /></div>
                <div className="text-sm font-semibold text-text-primary mb-1">Joyous, Not Enforced</div>
                <div className="text-xs text-text-secondary leading-snug">Make Judaism fun for your kids</div>
              </div>
              <div className="text-center">
                <div className="mb-3"><Icon name="🗓️" size="2x" className="text-primary" /></div>
                <div className="text-sm font-semibold text-text-primary mb-1">Fit Your Life</div>
                <div className="text-xs text-text-secondary leading-snug">Works with your busy schedule</div>
              </div>
              <div className="text-center">
                <div className="mb-3"><Icon name="🤝" size="2x" className="text-primary" /></div>
                <div className="text-sm font-semibold text-text-primary mb-1">Your Tribe</div>
                <div className="text-xs text-text-secondary leading-snug">Connect with families like yours</div>
              </div>
            </div>

            <div className="bg-white rounded-card p-6 sm:p-8 mb-6 sm:mb-8 relative shadow-card border border-border">
              <div className="text-base sm:text-lg leading-relaxed mb-3 sm:mb-4 italic text-text-primary">
                "I don't know how to begin the process. I want to incorporate Judaism, but I didn't grow up like this."
              </div>
              <div className="text-xs sm:text-sm text-text-secondary">— Parent of the 70%</div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={nextStep}
                className="flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Let's Get Started
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Family Name */}
        {step === 1 && (
          <div className="animate-fadeIn">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <div className="text-xs sm:text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3 sm:mb-4">
                Step 1 of 10
              </div>
              <h2 className="font-sans text-2xl sm:text-3xl md:text-3xl sm:text-4xl font-semibold text-text-primary mb-3 sm:mb-4 leading-tight tracking-tight">
                What's your family name?
              </h2>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                We'll use this to personalize everything for you.
              </p>
            </div>

            <div className="mb-8 sm:mb-10 md:mb-12">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Family Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-[14px] text-base font-sans border border-border rounded-xl bg-white text-text-primary focus:outline-none focus:border-primary transition-all"
                  value={formData.familyName}
                  onChange={(e) => setFormData({ ...formData, familyName: e.target.value })}
                  onKeyDown={handleKeyDown}
                  placeholder="The Goldberg Family"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Your Name + Partner Name */}
        {step === 2 && (
          <div className="animate-fadeIn">
            <div className="mb-6 sm:mb-8 md:mb-10">
              <div className="text-xs sm:text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3 sm:mb-4">
                Step 2 of 10
              </div>
              <h2 className="font-sans text-2xl sm:text-3xl md:text-3xl sm:text-4xl font-semibold text-text-primary mb-3 sm:mb-4 leading-tight tracking-tight">
                And your name?
              </h2>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                So we can greet you properly.
              </p>
            </div>

            <div className="mb-12">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Your First Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-[14px] text-base font-sans border border-border rounded-xl bg-white text-text-primary focus:outline-none focus:border-primary transition-all"
                  value={formData.yourName}
                  onChange={(e) => setFormData({ ...formData, yourName: e.target.value })}
                  onKeyDown={handleKeyDown}
                  placeholder="Rachel"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Partner's Name (Optional)
                </label>
                <p className="text-[13px] text-text-secondary mb-3 leading-relaxed">
                  If you're planning together, we'll invite them to collaborate.
                </p>
                <input
                  type="text"
                  className="w-full px-4 py-[14px] text-base font-sans border border-border rounded-xl bg-white text-text-primary focus:outline-none focus:border-primary transition-all"
                  value={formData.partnerName}
                  onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                  onKeyDown={handleKeyDown}
                  placeholder="David"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Kids */}
        {step === 3 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 3 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                Tell us about your kids
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                We'll suggest age-appropriate activities and content.
              </p>
            </div>

            <div className="mb-12">
              <div className="flex flex-col gap-4 mb-4">
                {formData.kids.map((kid, index) => (
                  <div
                    key={index}
                    className="bg-white border border-border rounded-xl p-5 grid grid-cols-[1fr_100px_40px] gap-3 items-center"
                  >
                    <input
                      type="text"
                      placeholder="Name"
                      className="border-none text-base font-sans text-text-primary focus:outline-none"
                      value={kid.name}
                      onChange={(e) => updateKid(index, 'name', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <input
                      type="number"
                      placeholder="Age"
                      className="border-none text-base font-sans text-text-primary focus:outline-none"
                      value={kid.age}
                      onChange={(e) => updateKid(index, 'age', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                    <button
                      onClick={() => removeKid(index)}
                      className="cursor-pointer text-text-secondary text-lg text-center hover:text-red-500 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addKid}
                className="w-full bg-surface border border-dashed border-border rounded-xl py-4 text-center cursor-pointer transition-all text-sm font-semibold text-text-secondary hover:border-primary hover:text-primary hover:bg-surface"
              >
                + Add Another Child
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Jewish Identity */}
        {step === 4 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 4 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                How would you describe your family's Jewish identity?
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                No judgment. Just helping us understand where you are.
              </p>
            </div>

            <div className="mb-8 sm:mb-10 md:mb-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[
                  {
                    value: 'culturally-jewish',
                    title: "We're culturally Jewish",
                    description: "We celebrate holidays and honor our heritage, but we're not religious or observant",
                  },
                  {
                    value: 'exploring',
                    title: "We're exploring what Judaism means to us",
                    description: "We're figuring out our own version and what feels right for our family",
                  },
                  {
                    value: 'interfaith',
                    title: "We're interfaith",
                    description: "One parent is Jewish, and we're creating a multi-cultural home",
                  },
                  {
                    value: 'reconnecting',
                    title: "We're reconnecting",
                    description: 'We distanced ourselves but want our kids to have a Jewish identity',
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => selectCard(option.value, 'identity')}
                    className={`text-left bg-white border rounded-card p-6 cursor-pointer transition-all hover:border-primary hover:-translate-y-0.5 ${
                      formData.identity === option.value
                        ? 'border-primary bg-surface'
                        : 'border-border'
                    }`}
                  >
                    <div className="text-base font-semibold text-text-primary mb-1.5">
                      {option.title}
                    </div>
                    <div className="text-[13px] text-text-secondary leading-snug">
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Starting Point */}
        {step === 5 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 5 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                Where are you starting from?
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                This helps us meet you where you are.
              </p>
            </div>

            <div className="mb-12">
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    value: 'just-beginning',
                    icon: '🌱',
                    title: 'Just Beginning',
                    description: "We don't do much Jewish stuff yet",
                  },
                  {
                    value: 'holidays-only',
                    icon: '🎂',
                    title: 'Holidays Only',
                    description: 'We celebrate Hanukkah, maybe Rosh Hashanah',
                  },
                  {
                    value: 'dabbling',
                    icon: '🕯️',
                    title: 'Dabbling',
                    description: 'Some holidays, occasional Shabbat dinner',
                  },
                  {
                    value: 'pretty-active',
                    icon: '⭐',
                    title: 'Pretty Active',
                    description: 'Regular rituals, teaching Hebrew, etc.',
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => selectCard(option.value, 'startingPoint')}
                    className={`text-left bg-white border rounded-card p-6 cursor-pointer transition-all hover:border-primary hover:-translate-y-0.5 ${
                      formData.startingPoint === option.value
                        ? 'border-primary bg-surface'
                        : 'border-border'
                    }`}
                  >
                    <div className="mb-3"><Icon name={option.icon} size="3x" className="text-primary" /></div>
                    <div className="text-base font-semibold text-text-primary mb-1.5">
                      {option.title}
                    </div>
                    <div className="text-[13px] text-text-secondary leading-snug">
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 6: What You're Hoping For */}
        {step === 6 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 6 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                What are you hoping for?
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                Select all that apply. This helps us prioritize the right content.
              </p>
            </div>

            <div className="mb-12">
              <div className="flex flex-col gap-3">
                {[
                  {
                    value: 'plan-celebrations',
                    label: 'Plan and host meaningful celebrations',
                    sublabel: 'Holidays, Shabbat, lifecycle events',
                  },
                  {
                    value: 'proud-identity',
                    label: 'Give my kids a proud Jewish identity',
                    sublabel: 'Without the boring, enforced version I grew up with',
                  },
                  {
                    value: 'teach-hebrew',
                    label: 'Teach Hebrew and cultural knowledge',
                    sublabel: 'Language, history, traditions',
                  },
                  {
                    value: 'family-rituals',
                    label: 'Create family rituals that feel authentic',
                    sublabel: "Our version, not someone else's",
                  },
                  {
                    value: 'find-community',
                    label: 'Find community with families like us',
                    sublabel: 'Progressive, non-religious, inclusive',
                  },
                  {
                    value: 'where-to-begin',
                    label: 'Figure out "where to begin"',
                    sublabel: 'I feel unprepared to teach Judaism',
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleCheckbox(option.value, 'goals')}
                    className={`text-left bg-white border rounded-xl p-4 cursor-pointer transition-all hover:border-primary flex items-center gap-3 ${
                      formData.goals.includes(option.value)
                        ? 'border-primary bg-surface'
                        : 'border-border'
                    }`}
                  >
                    <div
                      className={`w-6 h-6 border rounded-md flex-shrink-0 flex items-center justify-center transition-all ${
                        formData.goals.includes(option.value)
                          ? 'bg-primary border-primary'
                          : 'border-border'
                      }`}
                    >
                      {formData.goals.includes(option.value) && (
                        <span className="text-white text-base font-semibold">✓</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-[15px] font-medium text-text-primary mb-1">
                        {option.label}
                      </div>
                      <div className="text-[13px] text-text-secondary">{option.sublabel}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 7: Parenting Priorities */}
        {step === 7 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 7 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                What matters most to you as a parent?
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                Choose your top 3 priorities. This helps us speak to how you parent.
              </p>
            </div>

            <div className="mb-12">
              <div className="flex flex-col gap-3">
                {[
                  { value: 'emotional-wellbeing', label: 'Emotional wellbeing & resilience' },
                  { value: 'social-skills', label: 'Building social skills & friendships' },
                  { value: 'kindness-empathy', label: 'Kindness, empathy & respect for others' },
                  { value: 'diversity', label: 'Exposure to diversity & different cultures' },
                  { value: 'independence', label: 'Independence & self-sufficiency' },
                  { value: 'skill-building', label: 'Skill building & preparing for the future' },
                  { value: 'interests', label: 'Following their interests & passions' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleCheckboxMax(option.value, 'parentingPriorities', 3)}
                    className={`text-left bg-white border rounded-xl p-4 cursor-pointer transition-all hover:border-primary flex items-center gap-3 ${
                      formData.parentingPriorities.includes(option.value)
                        ? 'border-primary bg-surface'
                        : 'border-border'
                    }`}
                  >
                    <div
                      className={`w-6 h-6 border rounded-md flex-shrink-0 flex items-center justify-center transition-all ${
                        formData.parentingPriorities.includes(option.value)
                          ? 'bg-primary border-primary'
                          : 'border-border'
                      }`}
                    >
                      {formData.parentingPriorities.includes(option.value) && (
                        <span className="text-white text-base font-semibold">✓</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-[15px] font-medium text-text-primary">
                        {option.label}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 8: Time Commitment */}
        {step === 8 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 8 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                How much time can you realistically dedicate?
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                Be honest. We're all busy. This helps us suggest what fits your life.
              </p>
            </div>

            <div className="mb-8 sm:mb-10 md:mb-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {[
                  {
                    value: 'stretched-thin',
                    title: "We're stretched thin",
                    description: 'Need super easy, quick wins. 5-10 min activities max.',
                  },
                  {
                    value: 'some-time',
                    title: 'We can carve out some time',
                    description: 'Can do 15-30 min activities or occasional weekend experiences',
                  },
                  {
                    value: 'ready-to-commit',
                    title: "We're ready to commit",
                    description: 'Can plan weekly rituals and bigger celebrations',
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => selectCard(option.value, 'timeCommitment')}
                    className={`text-left bg-white border rounded-card p-6 cursor-pointer transition-all hover:border-primary hover:-translate-y-0.5 ${
                      formData.timeCommitment === option.value
                        ? 'border-primary bg-surface'
                        : 'border-border'
                    }`}
                  >
                    <div className="text-base font-semibold text-text-primary mb-1.5">
                      {option.title}
                    </div>
                    <div className="text-[13px] text-text-secondary leading-snug">
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 9: Upcoming Events */}
        {step === 9 && (
          <div className="animate-fadeIn">
            <div className="mb-10">
              <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-4">
                Step 9 of 10
              </div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-4 leading-tight tracking-tighter">
                What's on your radar?
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                Any upcoming events or milestones we should help you plan for?
              </p>
            </div>

            <div className="mb-12">
              <div className="flex flex-col gap-3">
                {[
                  { value: 'upcoming-holiday', label: 'Upcoming holiday (Purim, Passover, etc.)' },
                  { value: 'bar-bat-mitzvah', label: 'Bar/Bat Mitzvah planning' },
                  { value: 'shabbat-dinners', label: 'Starting Shabbat dinners' },
                  { value: 'baby-naming', label: 'Baby naming or bris' },
                  { value: 'just-exploring', label: 'Nothing specific - just exploring' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleCheckbox(option.value, 'upcomingEvents')}
                    className={`text-left bg-white border rounded-xl p-4 cursor-pointer transition-all hover:border-primary flex items-center gap-3 ${
                      formData.upcomingEvents.includes(option.value)
                        ? 'border-primary bg-surface'
                        : 'border-border'
                    }`}
                  >
                    <div
                      className={`w-6 h-6 border rounded-md flex-shrink-0 flex items-center justify-center transition-all ${
                        formData.upcomingEvents.includes(option.value)
                          ? 'bg-primary border-primary'
                          : 'border-border'
                      }`}
                    >
                      {formData.upcomingEvents.includes(option.value) && (
                        <span className="text-white text-base font-semibold">✓</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-[15px] font-medium text-text-primary">
                        {option.label}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={prevStep}
                className="w-full sm:w-auto bg-white text-text-secondary border border-border py-3 sm:py-[14px] px-6 sm:px-8 rounded-xl font-semibold hover:border-primary hover:text-primary transition-all text-sm sm:text-base"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="w-full sm:flex-1 bg-primary text-white font-semibold py-3 sm:py-[14px] px-6 sm:px-8 rounded-lg hover:bg-primary-dark transition-all text-sm sm:text-base shadow-button hover:shadow-button-hover"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 10: Complete */}
        {step === 10 && (
          <div className="animate-fadeIn">
            <div className="text-center py-[60px]">
              <div className="mb-8"><Icon name="✨" size="5x" className="text-primary" /></div>
              <h2 className="font-sans text-3xl sm:text-4xl font-semibold text-text-primary mb-5 tracking-tighter">
                Welcome to Your Jewish Life
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed mb-10">
                We're building your personalized dashboard now. This will just take a moment.
              </p>

              <div className="max-w-[300px] mx-auto">
                <button
                  onClick={handleComplete}
                  className="w-full bg-primary text-white font-semibold py-[14px] px-8 rounded-lg hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover"
                >
                  See My Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
